import React from 'react';
import { CheckIcon } from 'lucide-react';
import type { BillingPeriod, Plan } from '../types/plan';
import { annualPrice } from '../data/plans';
import { Button } from './ui/Button';
import { formatInterval } from '../utils/format';
interface PlanRowProps {
  plan: Plan;
  period: BillingPeriod;
  fits: boolean;
  onSelect: () => void;
}
export function PlanRow({
  plan,
  period,
  fits,
  onSelect
}: PlanRowProps) {
  const monthly = period === 'annual' ? annualPrice(plan) / 12 : plan.monthlyPrice;
  const fastest = plan.pingOptions[plan.pingOptions.length - 1];

  /* The recommended tier is inverted, so hierarchy is unmistakable. */
  const dark = plan.recommended;
  const specs = [{
    label: 'Services',
    value: String(plan.services)
  }, {
    label: 'Fastest ping',
    value: formatInterval(fastest).replace('Every ', '')
  }, {
    label: 'Recipients',
    value: plan.recipientLimit === 'unlimited' ? 'Unlimited' : String(plan.recipientLimit)
  }, {
    label: 'History',
    value: plan.historyLabel
  }];
  return <article className={`overflow-hidden rounded-card border transition-[border-color,box-shadow] duration-200 ease-snap ${dark ? 'border-shell-line bg-shell shadow-raised' : fits ? 'border-brand-border bg-surface shadow-card' : 'border-line bg-surface shadow-card'}`} data-id="element-396" data-mp="1qjapqvvfirq5">
      <div className="grid gap-6 p-6 lg:grid-cols-[1.1fr_1.5fr_auto] lg:items-center lg:gap-8 lg:p-7" data-id="element-397" data-mp="1ckkqpap1uhz5">
        <div data-id="element-398" data-mp="17rrksjhuh3lh">
          <div className="flex flex-wrap items-center gap-2" data-id="element-399" data-mp="17r86dcrekxc7">
            <h3 className={`text-xl font-semibold tracking-tight ${dark ? 'text-white' : 'text-ink'}`} data-id="element-400" data-mp="12t6imej189ue">
              {plan.name}
            </h3>
            {dark && <span className="rounded-full bg-brand-bright/15 px-2 py-0.5 font-mono text-[11px] font-medium text-brand-bright" data-id="element-401" data-mp="sv0l0949dpvc">
                recommended
              </span>}
            {fits && !dark && <span className="rounded-full bg-healthy-soft px-2 py-0.5 font-mono text-[11px] font-medium text-healthy-ink" data-id="element-402" data-mp="eaxvseoa86av">
                fits your sites
              </span>}
          </div>
          <p className={`mt-2 text-[13px] leading-relaxed ${dark ? 'text-white/60' : 'text-ink-muted'}`} data-id="element-403" data-mp="5ahv1cyxbd9p">
            {plan.tagline}
          </p>
        </div>

        <dl className={`grid grid-cols-2 gap-x-6 gap-y-3 border-y py-4 sm:grid-cols-4 lg:border-x lg:border-y-0 lg:px-8 lg:py-0 ${dark ? 'border-shell-line' : 'border-line'}`} data-id="element-404" data-mp="11yeageimk2jc">
          {specs.map(spec => <div key={spec.label} data-id="element-405" data-mp="t4j8ffcsus7x">
              <dt className={`font-mono text-[10px] uppercase tracking-[0.1em] ${dark ? 'text-white/40' : 'text-ink-soft'}`} data-id="element-406" data-mp="1gvomboz6jafl">
                {spec.label}
              </dt>
              <dd className={`mt-1.5 font-mono text-[15px] font-medium ${dark ? 'text-white' : 'text-ink'}`} data-id="element-407" data-mp="1v05e9wcacqna">
                {spec.value}
              </dd>
            </div>)}
        </dl>

        <div className="lg:w-[184px] lg:text-right" data-id="element-408" data-mp="1zs6xy3bs9jnc">
          <p className="flex items-baseline gap-1.5 lg:justify-end" data-id="element-409" data-mp="1zsgp3u8kdwum">
            <span className={`font-mono text-[2.2rem] font-medium leading-none ${dark ? 'text-white' : 'text-ink'}`} data-id="element-410" data-mp="1c1ksmjt5ydh1">
              ${monthly % 1 === 0 ? monthly : monthly.toFixed(2)}
            </span>
            <span className={`text-sm ${dark ? 'text-white/50' : 'text-ink-muted'}`} data-id="element-411" data-mp="1h1fgsflk0oye">
              /mo
            </span>
          </p>
          <p className={`mt-1.5 text-xs ${dark ? 'text-white/45' : 'text-ink-soft'}`} data-id="element-412" data-mp="2xe4c5oj4r2th">
            {period === 'annual' ? `$${annualPrice(plan)} billed yearly` : 'Billed monthly'}
          </p>
          <Button variant={dark ? 'bright' : 'secondary'} className="mt-4 w-full" onClick={onSelect} data-id="element-413" data-mp="16xsd2amio5qg">
            Choose {plan.name}
          </Button>
        </div>
      </div>

      <ul className={`flex flex-wrap gap-x-6 gap-y-2 border-t px-6 py-3.5 lg:px-7 ${dark ? 'border-shell-line bg-shell-raised/50' : 'border-line bg-canvas'}`} data-id="element-414" data-mp="1g0tgu4nwtkqy">
        {plan.highlights.slice(2).map(highlight => <li key={highlight} className={`flex items-center gap-1.5 text-xs ${dark ? 'text-white/60' : 'text-ink-muted'}`} data-id="element-415" data-mp="1uoekbzop5lze">
            <CheckIcon className={`h-3 w-3 shrink-0 ${dark ? 'text-brand-bright' : 'text-brand'}`} aria-hidden="true" data-id="element-416" data-mp="23oulv3lvqj1m" />
            {highlight}
          </li>)}
      </ul>
    </article>;
}